import { useState } from "react";
import { FileSpreadsheet, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { downloadBlob } from "@/lib/intake";
import { DropZone } from "@/components/intake/DropZone";
import { JobSelect } from "@/components/intake/JobSelect";

const OK = /\.(pdf|docx|zip|csv|xlsx)$/i;
const CHUNK = 10;

function Templates() {
  const dl = async (ext) => {
    try { downloadBlob((await api.get(`/intake/template.${ext}`, { responseType: "blob" })).data, `candidate-import-template.${ext}`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-ink-secondary">
      <FileSpreadsheet size={16} className="text-teal" aria-hidden="true" /> Spreadsheet template:
      <Button variant="link" className="h-auto p-0" onClick={() => dl("csv")} data-testid="template-csv">CSV</Button>
      <span aria-hidden="true">·</span>
      <Button variant="link" className="h-auto p-0" onClick={() => dl("xlsx")} data-testid="template-xlsx">XLSX</Button>
      <span className="text-xs text-ink-muted">Columns: name, email, phone, job, source, resume_url (optional), notes</span>
    </div>
  );
}

export function ImportUploadStep({ onStarted }) {
  const [job, setJob] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const add = (list) => {
    const bad = list.filter((f) => !OK.test(f.name));
    if (bad.length) toast.error(`Skipped ${bad.length} unsupported file(s). Use PDF, DOCX, ZIP, CSV or XLSX.`);
    setFiles((cur) => [...cur, ...list.filter((f) => OK.test(f.name))]);
  };
  const start = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); if (job) fd.append("job_id", job);
      const id = (await api.post("/intake/imports", fd)).data.import_id;
      for (let i = 0; i < files.length; i += CHUNK) {
        const chunk = new FormData(); files.slice(i, i + CHUNK).forEach((f) => chunk.append("files", f));
        await api.post(`/intake/imports/${id}/files`, chunk);
      }
      await api.post(`/intake/imports/${id}/finish-upload`);
      onStarted(id);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
      <section className="panel space-y-3 p-6" data-testid="import-step-job">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">Step 1</p>
        <h2 className="text-base font-semibold">Target job (optional)</h2>
        <JobSelect value={job} onChange={setJob} allowNone testId="import-job-select" />
        <p className="text-xs leading-relaxed text-ink-secondary">Without a job, candidates go to the talent pool with no application. A "job" column in a spreadsheet (title or careers slug) overrides this per row.</p>
      </section>
      <section className="panel space-y-4 p-6" data-testid="import-step-files">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">Step 2</p>
        <h2 className="text-base font-semibold">Add files</h2>
        <DropZone multiple accept=".pdf,.docx,.zip,.csv,.xlsx" onFiles={add} disabled={busy} testId="import-drop"
          title="Drop resumes, a ZIP or a spreadsheet" hint="PDF or DOCX up to 10 MB each · ZIP up to 200 MB · CSV or XLSX" />
        <Templates />
        {files.length > 0 && (
          <ul className="divide-y divide-line rounded-[6px] border border-line" data-testid="import-file-list">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="truncate">{f.name}</span>
                <button type="button" onClick={() => setFiles(files.filter((_, idx) => idx !== i))} aria-label={`Remove ${f.name}`} className="text-ink-muted hover:text-risk" disabled={busy}><X size={14} /></button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-end">
          <Button onClick={start} disabled={!files.length || busy} data-testid="import-start-btn">{busy && <Loader2 size={14} className="animate-spin" aria-hidden="true" />} {busy ? "Uploading…" : `Upload and check ${files.length || ""} file(s)`}</Button>
        </div>
      </section>
    </div>
  );
}
